import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Formlio
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Terms of Service</h1>
          <p className="text-slate-600 mt-2">Last updated: February 10, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-slate max-w-none">
          
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Service Description</h2>
            <p className="text-slate-700 leading-relaxed">
              Formlio is an online form builder and workflow automation platform that enables users to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>Create and customize online forms</li>
              <li>Collect and manage form submissions</li>
              <li>Integrate with third-party services such as Google Sheets, Google Calendar, 
                  Slack, Microsoft Teams, and payment providers</li>
              <li>Automate workflows based on form submissions</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              All integrations are optional and user-controlled. You choose which integrations to 
              enable and configure for each form.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Account Registration</h2>
            <p className="text-slate-700 leading-relaxed">
              To use Formlio, you must create an account by providing a valid email address and 
              password. You agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>Provide accurate and complete registration information</li>
              <li>Keep your account credentials secure and confidential</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Be responsible for all activities that occur under your account</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. User Responsibilities</h2>
            <p className="text-slate-700 leading-relaxed">
              As a user of Formlio, you are responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>Data Collection Compliance:</strong> Ensuring that your data collection 
                practices comply with all applicable data protection laws, including GDPR, CCPA, 
                and other relevant regulations.
              </li>
              <li>
                <strong>User Consent:</strong> Obtaining appropriate consent from individuals 
                whose data you collect through forms created on Formlio.
              </li>
              <li>
                <strong>Integration Permissions:</strong> Understanding and accepting the 
                permissions required by third-party integrations you enable.
              </li>
              <li>
                <strong>Content Responsibility:</strong> All content you create, collect, and 
                store using Formlio, including form fields, questions, and submission data.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Third-Party Services</h2>
            <p className="text-slate-700 leading-relaxed">
              Formlio integrates with third-party platforms to enhance functionality. By using 
              these integrations, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                Third-party services (including Google APIs, Slack, Microsoft, etc.) are governed 
                by their own terms of service and privacy policies.
              </li>
              <li>
                Formlio is not responsible for the availability, performance, or changes to 
                third-party services.
              </li>
              <li>
                If a third-party service experiences downtime or discontinues features, Formlio 
                cannot guarantee continued functionality of related integrations.
              </li>
              <li>
                You are responsible for complying with the terms of service of any third-party 
                platform you connect to through Formlio.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Data Ownership</h2>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 my-4">
              <p className="text-slate-800 font-medium">
                You retain full ownership of all data you create and collect using Formlio.
              </p>
            </div>
            <p className="text-slate-700 leading-relaxed">
              Specifically:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>Your Forms:</strong> All forms you create, including their structure, 
                questions, and design, belong to you.
              </li>
              <li>
                <strong>Submission Data:</strong> All data collected through your forms belongs 
                to you. Formlio does not claim any ownership rights over your submission data.
              </li>
              <li>
                <strong>Export Rights:</strong> You may export your data at any time.
              </li>
              <li>
                <strong>License to Operate:</strong> By using Formlio, you grant us a limited 
                license to store, process, and transmit your data solely for the purpose of 
                providing our services to you.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Acceptable Use</h2>
            <p className="text-slate-700 leading-relaxed">
              You agree NOT to use Formlio for:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>Any illegal activities or purposes</li>
              <li>Collecting data in violation of privacy laws</li>
              <li>Distributing malware, spam, or harmful content</li>
              <li>Attempting to bypass security measures or access other users' data</li>
              <li>Overloading our systems or conducting denial-of-service attacks</li>
              <li>Violating the terms of service of integrated third-party platforms</li>
              <li>Creating forms for phishing or fraudulent purposes</li>
              <li>Harvesting personal data without proper consent</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Subscription & Payments</h2>
            <p className="text-slate-700 leading-relaxed">
              Formlio offers both free and paid subscription plans:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>Free Plan:</strong> Limited features and usage quotas as described on 
                our pricing page.
              </li>
              <li>
                <strong>Paid Plans:</strong> Additional features, higher quotas, and premium 
                integrations. Payment terms are displayed at checkout.
              </li>
              <li>
                <strong>Billing:</strong> Paid subscriptions are billed in advance on a monthly 
                or annual basis.
              </li>
              <li>
                <strong>Cancellation:</strong> You may cancel your subscription at any time. 
                Access to paid features continues until the end of the current billing period.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-4">
              <p className="text-slate-800">
                FORMLIO IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED.
              </p>
            </div>
            <p className="text-slate-700 leading-relaxed">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                We do not guarantee uninterrupted, error-free, or secure operation of our services.
              </li>
              <li>
                We are not liable for any indirect, incidental, special, consequential, or 
                punitive damages.
              </li>
              <li>
                Our total liability for any claim arising from these terms or your use of 
                Formlio shall not exceed the amount you paid to us in the 12 months preceding 
                the claim.
              </li>
              <li>
                We are not responsible for data loss due to third-party service failures, 
                user error, or circumstances beyond our control.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Indemnification</h2>
            <p className="text-slate-700 leading-relaxed">
              You agree to indemnify and hold harmless Formlio, its officers, directors, employees, 
              and agents from any claims, damages, losses, or expenses (including legal fees) 
              arising from:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>Your use of Formlio</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Data you collect or process using our services</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Termination</h2>
            <p className="text-slate-700 leading-relaxed">
              Either party may terminate this agreement:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>By You:</strong> You may stop using Formlio and delete your account at 
                any time.
              </li>
              <li>
                <strong>By Us:</strong> We reserve the right to suspend or terminate your account 
                for violations of these Terms, with or without notice depending on the severity 
                of the violation.
              </li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Upon termination, your right to use Formlio ceases immediately. We may retain 
              certain data as required by law or for legitimate business purposes.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Changes to Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update these Terms of Service from time to time. When we make significant 
              changes:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>We will post the updated terms on this page with a new "Last updated" date.</li>
              <li>For material changes, we will notify you via email or a prominent notice on 
                  our website.</li>
              <li>Your continued use of Formlio after changes constitutes acceptance of the 
                  updated terms.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Governing Law</h2>
            <p className="text-slate-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with applicable laws, 
              without regard to conflict of law principles. Any disputes shall be resolved in the 
              appropriate courts of the jurisdiction where Formlio operates.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="mt-4 text-slate-700">
              <p><strong>Email:</strong> support@formlio.app</p>
              <p className="mt-2"><strong>Website:</strong> https://formlio.app</p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TermsPage;
